// Modal "Upload de banco via IA" — recebe um PDF do aluno e usa o prompt
// universal de extração pra estruturar em JSON de banco. ASSÍNCRONO desde
// 2026-05-30: a extração roda num job no backend (cron) porque levava 40-90s e
// estourava o timeout do Worker (request "Canceled" → "Serviço fora do ar").
//
// Fluxo:
//   1. "input": user seleciona/arrasta PDF + contexto/gabarito → POST /api/ai/ingest-bank
//      devolve 202 { jobId }.
//   2. "extracting": polling em /api/ai/ingest-bank/status. O aluno pode FECHAR
//      o modal — o cron cria o banco mesmo assim e o sininho avisa.
//   3. "done": banco já criado (privado) → botão "Abrir banco".
//
// O banco nasce PRIVADO. Sem etapa de revisão (decisão 2026-05-30): o cron
// cria direto e o aluno edita/apaga depois se quiser.

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  Sparkles,
  Upload,
  X
} from "lucide-react";
import { call } from "./api";
import { friendlyError } from "@app/ui/lib/friendly-error";
import { loadBanks } from "./App";
import { openBank } from "./BankDetailView";

const MAX_PDF_BYTES = 10_000_000; // 10MB — bate com MAX_PDF_BYTES do backend
const POLL_INTERVAL_MS = 2500;

export const uploadBankModalOpen = signal<boolean>(false);

export function openUploadBankModal() {
  uploadBankModalOpen.value = true;
}

export function UploadBankModal() {
  if (!uploadBankModalOpen.value) return null;
  return <ModalBody onClose={() => (uploadBankModalOpen.value = false)} />;
}

function ModalBody({ onClose }: { onClose: () => void }) {
  const [phase, setPhase] = useState<"input" | "extracting" | "done">("input");
  const [file, setFile] = useState<File | null>(null);
  const [hint, setHint] = useState("");
  // Texto livre com gabarito/observações/anuladas. A IA trata como autoridade
  // ao resolver discrepâncias com o PDF. [[project-upload-ia-aux-inputs-and-processing]]
  const [auxText, setAuxText] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [createdBankId, setCreatedBankId] = useState<string | null>(null);
  const [createdCount, setCreatedCount] = useState<number>(0);

  // Polling do job enquanto "extracting". Para sozinho ao desmontar (fechar).
  useEffect(() => {
    if (phase !== "extracting" || !jobId) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async () => {
      try {
        const s = await call<{
          status: "queued" | "running" | "done" | "error" | "cancelled";
          bankId: string | null;
          bankName: string | null;
          count: number | null;
          error: string | null;
        }>(`/api/ai/ingest-bank/status?jobId=${encodeURIComponent(jobId)}`);
        if (stopped) return;
        if (s.status === "done") {
          setCreatedBankId(s.bankId);
          setCreatedCount(s.count ?? 0);
          void loadBanks({ silent: true });
          setPhase("done");
          return;
        }
        if (s.status === "error") {
          setErr(s.error ?? "Falha na extração.");
          setPhase("input");
          return;
        }
        if (s.status === "cancelled") return;
        timer = setTimeout(() => void poll(), POLL_INTERVAL_MS);
      } catch {
        if (!stopped) timer = setTimeout(() => void poll(), POLL_INTERVAL_MS);
      }
    };
    timer = setTimeout(() => void poll(), 2000);
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, [phase, jobId]);

  async function extract() {
    setErr(null);
    if (!file) {
      setErr("Selecione um PDF antes de extrair.");
      return;
    }
    if (file.size > MAX_PDF_BYTES) {
      setErr(
        `PDF de ${(file.size / 1_000_000).toFixed(1)}MB ultrapassa o limite de 10MB.`
      );
      return;
    }
    setPhase("extracting");
    try {
      const pdfBase64 = await fileToBase64(file);
      const res = await call<{ jobId: string; status: string }>(
        "/api/ai/ingest-bank",
        {
          method: "POST",
          body: JSON.stringify({
            pdfBase64,
            filename: file.name,
            hint: hint.trim() || undefined,
            auxText: auxText.trim() || undefined
          })
        }
      );
      setJobId(res.jobId); // dispara o polling no useEffect
    } catch (err) {
      // Erros de validação (size/credits/config) voltam aqui na hora.
      setErr(friendlyError(err));
      setPhase("input");
    }
  }

  function openCreated() {
    if (createdBankId) openBank(createdBankId);
    onClose();
  }

  return (
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div class="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-lg border border-border bg-card shadow-xl">
        <header class="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 class="flex items-center gap-2 text-base font-bold">
            <Sparkles className="size-5 text-primary" /> Upload de banco via IA
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="grid size-8 place-items-center rounded-md text-muted-foreground hover:bg-accent"
          >
            <X className="size-4" />
          </button>
        </header>

        <div class="flex-1 overflow-y-auto px-5 py-4">
          {phase === "input" && (
            <InputStep
              file={file}
              setFile={setFile}
              hint={hint}
              setHint={setHint}
              auxText={auxText}
              setAuxText={setAuxText}
              err={err}
              onSubmit={extract}
            />
          )}
          {phase === "extracting" && <ExtractingStep onClose={onClose} />}
          {phase === "done" && (
            <DoneStep count={createdCount} onOpen={openCreated} />
          )}
        </div>
      </div>
    </div>
  );
}

function InputStep({
  file,
  setFile,
  hint,
  setHint,
  auxText,
  setAuxText,
  err,
  onSubmit
}: {
  file: File | null;
  setFile: (f: File | null) => void;
  hint: string;
  setHint: (s: string) => void;
  auxText: string;
  setAuxText: (s: string) => void;
  err: string | null;
  onSubmit: () => void;
}) {
  const [drag, setDrag] = useState(false);

  function handlePicked(f: File | null) {
    if (!f) return;
    if (!/\.pdf$/i.test(f.name) && f.type !== "application/pdf") {
      setFile(null);
      alert("Selecione um arquivo PDF.");
      return;
    }
    setFile(f);
  }

  return (
    <div class="space-y-3">
      <p class="text-sm text-muted-foreground">
        Envie o <strong>PDF</strong> do material (prova, lista, simulado) e a
        IA estrutura tudo em questões com gabarito e explicação. O banco é
        criado privado e a gente te avisa pelo sininho quando ficar pronto.
      </p>

      <label
        class={`relative block cursor-pointer rounded-lg border-2 border-dashed px-6 py-8 text-center transition-colors ${
          drag
            ? "border-primary bg-primary/5"
            : "border-border bg-background hover:border-primary/50 hover:bg-primary/5"
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          const f = e.dataTransfer?.files?.[0];
          if (f) handlePicked(f);
        }}
      >
        <input
          type="file"
          accept="application/pdf,.pdf"
          class="absolute inset-0 cursor-pointer opacity-0"
          aria-label="Selecionar PDF"
          onChange={(e) => {
            const f = (e.target as HTMLInputElement).files?.[0] ?? null;
            handlePicked(f);
          }}
        />
        {file ? (
          <div class="space-y-1">
            <CheckCircle2 className="mx-auto size-6 text-emerald-500" />
            <p class="text-sm font-semibold">{file.name}</p>
            <p class="text-xs text-muted-foreground">
              {(file.size / 1_000_000).toFixed(2)} MB · Clique pra trocar
            </p>
          </div>
        ) : (
          <div class="space-y-1">
            <Upload className="mx-auto size-6 text-muted-foreground" />
            <p class="text-sm font-semibold">
              Arraste o PDF aqui ou clique pra selecionar
            </p>
            <p class="text-xs text-muted-foreground">
              Até 10MB. Quanto melhor a qualidade do PDF, melhor a extração.
            </p>
          </div>
        )}
      </label>

      <label class="block">
        <span class="block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          Contexto (opcional)
        </span>
        <input
          type="text"
          value={hint}
          maxLength={500}
          onInput={(e) => setHint((e.target as HTMLInputElement).value)}
          placeholder="ex: ENEM 2023 Matemática, Concurso CRC SP, Simulado UFMG 2024"
          aria-label="Contexto"
          class="mt-1 w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
        />
        <span class="mt-1 block text-[10px] text-muted-foreground">
          Vira o título do banco quando preenchido. Ajuda também a IA a se
          situar (banca, ano, disciplina).
        </span>
      </label>

      <label class="block">
        <span class="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          <Sparkles className="size-3 text-violet-500" />
          Gabarito e observações (opcional)
        </span>
        <textarea
          value={auxText}
          maxLength={20_000}
          rows={4}
          onInput={(e) => setAuxText((e.target as HTMLTextAreaElement).value)}
          placeholder={`Cole aqui o gabarito oficial, errata ou instruções de questões anuladas. Ex:\n\nQuestão 5 — anulada\nQuestão 12 — gabarito oficial é B (apesar do PDF)\nQuestão 18 — anulada por dubiedade`}
          aria-label="Gabarito e observações"
          class="mt-1 w-full resize-y rounded-md border border-violet-500/40 bg-violet-500/5 px-3 py-2 text-xs outline-none focus:border-violet-500"
        />
        <span class="mt-1 block text-[10px] text-muted-foreground">
          A IA trata este bloco como <strong>autoridade</strong> sobre o PDF
          quando houver conflito. Use pra corrigir gabarito errado, marcar
          anuladas e adicionar comentários que devem aparecer nas explicações.
        </span>
      </label>

      {err && (
        <p class="flex items-center gap-1.5 text-xs text-destructive">
          <AlertCircle className="size-3.5" /> {err}
        </p>
      )}

      <div class="space-y-2">
        <div class="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-700 dark:text-amber-300">
          A extração leva de 30 a 90 segundos e roda em segundo plano. Você
          pode <strong>fechar esta janela</strong> e continuar usando — vamos
          te avisar pelo <strong>sininho de notificações</strong> quando o
          banco estiver pronto.
        </div>
        <div class="flex items-start gap-2 rounded-md border border-blue-500/30 bg-blue-500/5 px-3 py-2 text-[11px] text-blue-700 dark:text-blue-300">
          <CheckCircle2 className="mt-0.5 size-3.5 shrink-0" />
          <span>
            <strong>Privacidade:</strong> seu banco nasce <strong>privado</strong>
            {" "}(só você vê). Você pode compartilhar com colegas ou tornar
            público depois — pelo botão <em>Compartilhar</em> dentro do banco.
          </span>
        </div>
      </div>

      <div class="flex justify-end">
        <button
          type="button"
          onClick={onSubmit}
          disabled={!file}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:brightness-110 disabled:opacity-50"
        >
          <Sparkles className="size-3.5" />
          Extrair questões
        </button>
      </div>
    </div>
  );
}

function ExtractingStep({ onClose }: { onClose: () => void }) {
  return (
    <div class="flex flex-col items-center justify-center gap-3 py-12 text-center">
      <Loader2 className="size-8 animate-spin text-primary" />
      <p class="text-sm font-medium">Extraindo questões do PDF…</p>
      <p class="max-w-md text-xs text-muted-foreground">
        A IA está analisando o documento (texto, tabelas, gráficos) e
        estruturando em questões. Leva de 30 a 90 segundos. Você pode fechar
        esta janela — o banco será criado e o sininho vai te avisar.
      </p>
      <button
        type="button"
        onClick={onClose}
        class="mt-2 rounded-md border border-border bg-background px-4 py-1.5 text-sm font-medium hover:bg-accent"
      >
        Fechar e aguardar no sininho
      </button>
    </div>
  );
}

function DoneStep({ count, onOpen }: { count: number; onOpen: () => void }) {
  return (
    <div class="flex flex-col items-center justify-center gap-3 py-8 text-center">
      <div class="grid size-14 place-items-center rounded-full bg-emerald-500/10 text-emerald-600">
        <CheckCircle2 className="size-7" />
      </div>
      <p class="text-base font-bold">Banco criado!</p>
      <p class="max-w-sm text-xs text-muted-foreground">
        {count > 0 ? `${count} questões extraídas. ` : ""}
        Já está disponível em "Bancos de questões". Você pode estudar, simular
        prova e exportar PDF normalmente.
      </p>
      <button
        type="button"
        onClick={onOpen}
        className="mt-2 inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:brightness-110"
      >
        Abrir banco
      </button>
    </div>
  );
}

// FileReader.readAsDataURL → strip "data:application/pdf;base64,"
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result || "");
      const comma = dataUrl.indexOf(",");
      resolve(comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl);
    };
    reader.onerror = () => reject(reader.error ?? new Error("FileReader falhou"));
    reader.readAsDataURL(file);
  });
}
